import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { services } from "@/data/services";

// Map slug -> bento span/tone (kept here to preserve original layout)
const layout = {
  "full-installations": { span: "lg:col-span-7 lg:row-span-2", tone: "dark", image: true },
  "grid-connections": { span: "lg:col-span-5", tone: "light" },
  "battery-installs": { span: "lg:col-span-5", tone: "light" },
  "micro-inverters": { span: "lg:col-span-4", tone: "light" },
  inverters: { span: "lg:col-span-3", tone: "light" },
};

const Services = () => {
  return (
    <section id="services" className="relative py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.3em] text-[#5C6B61]">
              · Our services
            </div>
            <h2
              data-testid="services-heading"
              className="font-display text-4xl sm:text-5xl tracking-tight font-medium mt-4 text-[#1A3626]"
            >
              Everything solar, under one roof.
            </h2>
          </div>
          <p className="text-base text-[#5C6B61] max-w-md leading-relaxed">
            We handle design, paperwork, installation and ongoing support — so
            you can sit back and watch the sun do its thing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 lg:gap-6 auto-rows-[minmax(180px,auto)]">
          {services.map((s) => {
            const Icon = s.icon;
            const cfg = layout[s.slug] || { span: "lg:col-span-4", tone: "light" };
            const isDark = cfg.tone === "dark";
            return (
              <Link
                key={s.id}
                to={`/services/${s.slug}`}
                data-testid={`service-card-${s.id}`}
                className={`relative overflow-hidden rounded-3xl border transition-all duration-500 hover:-translate-y-1 group ${
                  cfg.span
                } ${
                  isDark
                    ? "border-[#1A3626] bg-[#1A3626] text-white"
                    : "border-[#D2D7D0] bg-white text-[#1A3626] hover:border-[#1A3626]/40"
                }`}
                style={{ boxShadow: "0 8px 32px rgba(26,54,38,0.06)" }}
              >
                {cfg.image && s.image && (
                  <div className="absolute inset-0 -z-0">
                    <img src={s.image} alt="" className="w-full h-full object-cover opacity-70 group-hover:opacity-80 transition-opacity" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A3626] via-[#1A3626]/60 to-transparent" />
                  </div>
                )}

                <div className="relative z-10 p-7 lg:p-9 h-full flex flex-col justify-between min-h-[200px]">
                  <div
                    className={`h-11 w-11 rounded-2xl flex items-center justify-center ${
                      isDark
                        ? "bg-[#F5C242] text-[#1A3626]"
                        : "bg-[#E8EAE6] text-[#1A3626] group-hover:bg-[#F5C242]"
                    } transition-colors`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="mt-10">
                    <h3 className="font-display text-2xl lg:text-3xl tracking-tight font-medium">
                      {s.title}
                    </h3>
                    <p
                      className={`mt-3 text-sm leading-relaxed max-w-md ${
                        isDark ? "text-white/80" : "text-[#5C6B61]"
                      }`}
                    >
                      {s.short}
                    </p>

                    <div
                      data-testid={`service-card-cta-${s.id}`}
                      className={`mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] ${
                        isDark ? "text-[#F5C242]" : "text-[#1A3626]"
                      }`}
                    >
                      Learn more
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
